/* Iter 203 — Order Register Audit Log (standalone page).
 * Opened from the Order Register toolbar "Audit Log" button in a new tab.
 * Admin sees every user's actions; standard users only their own.
 * Entries auto-expire after 365 days (MongoDB TTL on created_at).
 */
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { BackBar } from "@/components/BackBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { RefreshCw, ScrollText, Eye, Lock, ArrowLeftRight } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const ACTION_META = {
  create:              { label: "Create",              cls: "bg-emerald-100 text-emerald-700" },
  update:              { label: "Update",              cls: "bg-blue-100 text-blue-700" },
  delete:              { label: "Delete",              cls: "bg-red-100 text-red-700" },
  bulk_delete:         { label: "Bulk Delete",         cls: "bg-red-600 text-white" },
  cancel:              { label: "Cancel Parcel",       cls: "bg-rose-100 text-rose-700" },
  restore:             { label: "Restore",             cls: "bg-teal-100 text-teal-700" },
  upload:              { label: "Excel Upload",        cls: "bg-purple-100 text-purple-700" },
  invoice_status:      { label: "Invoice Status",      cls: "bg-amber-100 text-amber-800" },
  packing_slip_deferred: { label: "Slip Upload Deferred", cls: "bg-rose-100 text-rose-800" },
  invoice_rename:      { label: "Invoice Rename",      cls: "bg-orange-100 text-orange-700" },
  bulk_invoice_assign: { label: "Bulk Invoice Assign", cls: "bg-indigo-100 text-indigo-700" },
  invoice_handover:    { label: "Ownership Change",    cls: "bg-cyan-100 text-cyan-800" },
  invoice_release:     { label: "Invoice Released",    cls: "bg-cyan-50 text-cyan-700" },
  bulk_cancel:         { label: "Bulk Cancel",         cls: "bg-rose-600 text-white" },
  bulk_reason:         { label: "Bulk Reason",         cls: "bg-amber-50 text-amber-700" },
  parcel_note_add:     { label: "Team Note Added",     cls: "bg-yellow-100 text-yellow-800" },
  parcel_note_delete:  { label: "Team Note Deleted",   cls: "bg-yellow-200 text-yellow-900" },
  parcel_note_colour:  { label: "Note Colour",         cls: "bg-yellow-50 text-yellow-700" },
  parcel_highlight:    { label: "Highlight Set",       cls: "bg-lime-100 text-lime-800" },
  parcel_highlight_clear: { label: "Highlights Cleared", cls: "bg-lime-50 text-lime-700" },
  query_open:          { label: "Query Opened",        cls: "bg-orange-100 text-orange-700" },
  query_note:          { label: "Query Message",       cls: "bg-orange-50 text-orange-700" },
  query_followup:      { label: "Query Follow-up",     cls: "bg-orange-50 text-orange-700" },
  query_outcome:       { label: "Query Solved",        cls: "bg-emerald-100 text-emerald-700" },
  query_reopen:        { label: "Query Reopened",      cls: "bg-orange-100 text-orange-800" },
  query_processed:     { label: "Query Processed",     cls: "bg-blue-100 text-blue-700" },
  upload_overwrite:    { label: "Upload Overwrite",    cls: "bg-purple-50 text-purple-700" },
  upload_overwrite_restore: { label: "Overwrite Restore", cls: "bg-teal-50 text-teal-700" },
  revert:              { label: "Undo / Revert",       cls: "bg-slate-200 text-slate-700" },
};

// Filter options — some group several actions so nothing is missed.
const FILTERS = [
  ["delete,bulk_delete", "Delete (all)"], ["cancel,bulk_cancel", "Cancel (all)"],
  ["restore,upload_overwrite_restore", "Restore (all)"], ["update", "Update"], ["create", "Create"],
  ["upload,upload_overwrite", "Uploads"], ["invoice_status", "Invoice Status"],
  ["invoice_rename,bulk_invoice_assign", "Invoice # changes"], ["invoice_handover,invoice_release", "Ownership"],
  ["bulk_reason", "Bulk Reason"], ["packing_slip_deferred", "Slip Upload Deferred"], ["revert", "Undo / Revert"],
  ["parcel_note_add,parcel_note_delete,parcel_note_colour", "Team notes"],
  ["parcel_highlight,parcel_highlight_clear", "Highlights"],
  ["query_open,query_note,query_followup,query_outcome,query_reopen,query_processed", "Queries"],
];

const fmtWhen = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return String(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

// Iter 206 - human-readable one-line summary for a bulk_delete filter.
// Powers the "scope" chip on Bulk Delete rows so supervisors can see at
// a glance who deleted what (date-wise / client / country / all data).
export function scopeSummary(extra) {
  const f = extra?.filter;
  if (!f || typeof f !== "object") return null;
  if (f.all) return { label: "All data", tone: "danger" };
  const parts = [];
  if (f.date_from || f.date_to) {
    parts.push(`dates ${f.date_from || "…"} → ${f.date_to || "…"}`);
  }
  if (f.client_code) parts.push(`client ${f.client_code}`);
  if (f.country_group) parts.push(`country ${String(f.country_group).replace(/_/g, " ")}`);
  if (f.mode) parts.push(`mode ${f.mode}`);
  if (f.reason) parts.push(`reason "${f.reason}"`);
  if (f.invoice_no) parts.push(`invoice ${f.invoice_no}`);
  if (f.no_tracking) parts.push("no tracking");
  if (f.no_invoice) parts.push("no invoice");
  if (f.no_second_name) parts.push("no second name");
  if (f.q) parts.push(`search "${f.q}"`);
  if (!parts.length) return null;
  return { label: parts.join(" · "), tone: "normal" };
}

function detailLines(row) {
  const lines = [];
  const ch = row.changes || {};
  const fmtV = (x) => (x && typeof x === "object"
    ? Object.entries(x).filter(([, y]) => y && (typeof y !== "object" || Object.keys(y).length))
        .map(([a, y]) => `${a}=${typeof y === "object" ? Object.entries(y).map(([c, z]) => `${c}:${z}`).join(",") : y}`).join(" ") || "none"
    : (x ?? "none"));
  for (const [k, v] of Object.entries(ch)) {
    lines.push(`${k}: "${fmtV(v?.before ?? v?.from)}" → "${fmtV(v?.after ?? v?.to)}"`);
  }
  const ex = row.extra || {};
  // Bulk delete scope is now rendered as a dedicated chip - skip `filter`
  // in the freeform detail lines so the row stays clean.
  const shown = row.parcel ? ["customer_name", "code", "medicine", "quantity", "second_name", "tracking"] : [];
  const pick = ["customer_name", "code", "medicine", "quantity", "second_name",
                "tracking", "reason", "parcels", "rows", "cancelled_rows", "count", "restored", "skipped", "filename",
                "inserted", "matched", "updated", "invoice_date", "source", "text", "target", "note_colour", "note_by", "query_id"];
  for (const k of pick) {
    if (shown.includes(k)) continue;  // already in the Parcel column
    const v = ex[k];
    if (v !== undefined && v !== "" && v !== null && typeof v !== "object") lines.push(`${k}: ${v}`);
  }
  if (Array.isArray(ex.customers) && ex.customers.length)
    lines.push(`customers: ${ex.customers.join(", ")}`);
  if (Array.isArray(ex.invoice_nos) && ex.invoice_nos.length)
    lines.push(`invoices: ${ex.invoice_nos.join(", ")}`);
  if (Array.isArray(ex.details))
    ex.details.slice(0, 20).forEach(d => d && typeof d === "object" &&
      lines.push(`• ${[d.customer_name, d.code, d.tracking, d.invoice_no].filter(Boolean).join(" · ")}`));
  return lines;
}

export default function OrderRegisterAuditPage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [action, setAction] = useState("");
  const [days, setDays] = useState("365");
  const [guardedOnly, setGuardedOnly] = useState(false);
  // Iter 207 - "View deleted rows" popover for bulk_delete entries.
  const [delView, setDelView] = useState(null);  // {loading, rows, error, meta}

  const viewDeletedRows = async (batchId, count) => {
    setDelView({ loading: true, rows: [], error: "", count });
    try {
      const r = await axios.get(
        `${API}/order-register/deleted-rows/${encodeURIComponent(batchId)}`,
        { headers: getAuthHeader() });
      setDelView({ loading: false, rows: r.data.rows || [], error: "",
                   count: r.data.count ?? 0,
                   deletedBy: r.data.deleted_by || "" });
    } catch (e) {
      setDelView({ loading: false, rows: [], count,
                   error: e?.response?.data?.detail ||
                          "Could not load snapshots" });
    }
  };
  const debounceRef = useRef(null);
  const [extraRows, setExtraRows] = useState([]);
  const loadMore = async () => {
    const r = await axios.get(`${API}/order-register/audit`, { headers: getAuthHeader(),
      params: { days, limit: 500, skip: (data?.rows.length || 0) + extraRows.length, action: action || undefined,
                q: q || undefined, guarded: guardedOnly || undefined } });
    setExtraRows(x => [...x, ...(r.data.rows || [])]);
  };
  const exportExcel = async () => {
    const r = await axios.get(`${API}/order-register/audit/excel`, { headers: getAuthHeader(), responseType: "blob",
      params: { days, action: action || undefined, q: q || undefined, guarded: guardedOnly || undefined } });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data); a.download = "order_register_audit.xlsx"; a.click();
  };
  const cnt = (keys) => keys.split(",").reduce((n, k) => n + (data?.counts?.[k] || 0), 0);
  const me = (() => {
    try { return JSON.parse(localStorage.getItem("user") || "{}"); }
    catch { return {}; }
  })();
  const isAdmin = me.role === "admin";

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/order-register/audit`, {
        headers: getAuthHeader(),
        params: { days, limit: 500, action: action || undefined,
                  q: q || undefined, guarded: guardedOnly || undefined },
      });
      setData(r.data);
      setExtraRows([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load audit log");
    } finally { setBusy(false); }
  }, [q, action, days, guardedOnly]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(load, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [load]);

  return (
    <div className="min-h-screen bg-slate-50 p-4 pt-0 sm:p-6 sm:pt-0" data-testid="or-audit-page">
      <BackBar title="Order Register — Audit Log" />
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <ScrollText className="w-5 h-5 text-indigo-600" /> Order Register — Audit Log
          </h1>
          <span className="text-xs text-slate-500" data-testid="or-audit-note">
            Kept for 365 days{isAdmin ? " · showing all users" : " · showing your actions only"}
          </span>
          {data && (
            <span className="text-xs text-slate-600" data-testid="or-audit-count">
              <b className="text-slate-800">{data.total}</b> entr{data.total === 1 ? "y" : "ies"}
            </span>
          )}
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" onClick={exportExcel} data-testid="or-audit-excel">Excel</Button>
            <Button size="sm" variant="outline" onClick={load} disabled={busy}
                    data-testid="or-audit-refresh">
              {busy ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Loading…</>
                    : <><RefreshCw className="w-3 h-3 mr-1" /> Refresh</>}
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap bg-white border rounded p-3">
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search customer, tracking, invoice #, user, reason…"
                 className="h-8 w-64" data-testid="or-audit-search" />
          <Select value={action || "__all__"}
                  onValueChange={(v) => setAction(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-56 h-8" data-testid="or-audit-action-filter">
              <SelectValue placeholder="All actions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All actions</SelectItem>
              {FILTERS.map(([k, l]) => (
                <SelectItem key={k} value={k}>{l}{data?.counts ? ` (${cnt(k)})` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-36 h-8" data-testid="or-audit-days-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last 24 hours</SelectItem>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="180">Last 180 days</SelectItem>
              <SelectItem value="365">Last 365 days</SelectItem>
            </SelectContent>
          </Select>
          {/* Iter 211 - Password-guarded chip: supervisors can isolate the
              sensitive tracking / invoice edits in one click */}
          <button type="button"
            onClick={() => setGuardedOnly(v => !v)}
            title="Show only edits that required the user's login password (locked tracking / invoice # changes)"
            className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-semibold border transition-colors ${
              guardedOnly
                ? "bg-rose-600 text-white border-rose-600"
                : "bg-white text-rose-700 border-rose-300 hover:bg-rose-50"
            }`}
            data-testid="or-audit-guarded-chip">
            <Lock className="w-3 h-3" /> Password-guarded
          </button>
          {/* Iter 220 — Ownership chip: one click to review every invoice
              handover (who transferred what, to whom, when). */}
          <button type="button"
            onClick={() => setAction(a => a === "invoice_handover,invoice_release" ? "" : "invoice_handover,invoice_release")}
            title="Show only invoice ownership transfers (admin handovers)"
            className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-semibold border transition-colors ${
              action === "invoice_handover,invoice_release"
                ? "bg-cyan-600 text-white border-cyan-600"
                : "bg-white text-cyan-700 border-cyan-300 hover:bg-cyan-50"
            }`}
            data-testid="or-audit-ownership-chip">
            <ArrowLeftRight className="w-3 h-3" /> Ownership changes
          </button>
        </div>

        <div className="bg-white border rounded overflow-hidden">
          <div className="max-h-[75vh] overflow-y-auto">
            <table className="w-full text-xs" data-testid="or-audit-table">
              <thead className="bg-slate-100 sticky top-0 z-10 text-slate-600 uppercase text-[10px] tracking-wide">
                <tr>
                  <th className="px-3 py-2 text-left w-32">When</th>
                  <th className="px-3 py-2 text-left w-48">User</th>
                  <th className="px-3 py-2 text-left w-36">Action</th>
                  <th className="px-3 py-2 text-left w-32">Invoice #</th>
                  <th className="px-3 py-2 text-left w-56">Parcel</th>
                  <th className="px-3 py-2 text-left w-56">Reason</th>
                  <th className="px-3 py-2 text-left">Details</th>
                </tr>
              </thead>
              <tbody>
                {(!data || busy) && (
                  <tr><td colSpan={7} className="text-center text-slate-500 py-8">
                    <RefreshCw className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                  </td></tr>
                )}
                {data && !busy && data.rows.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-slate-500 py-8"
                          data-testid="or-audit-empty">
                    No audit entries in this window.
                  </td></tr>
                )}
                {data && !busy && [...data.rows, ...extraRows].map((r) => {
                  const meta = ACTION_META[r.action] || { label: r.action, cls: "bg-slate-100 text-slate-600" };
                  const lines = detailLines(r);
                  return (
                    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50 align-top"
                        data-testid={`or-audit-row-${r.id}`}>
                      <td className="px-3 py-2 whitespace-nowrap text-slate-600">{fmtWhen(r.created_at)}</td>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-slate-700">{r.user_name || "—"}</div>
                        <div className="text-slate-400 text-[10px]">{r.user_email}</div>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded font-semibold text-[10px] ${meta.cls}`}
                              data-testid={`or-audit-action-${r.id}`}>
                          {meta.label}
                          {r.extra?.via_login_password && (
                            <Lock className="w-2.5 h-2.5" data-testid={`or-audit-guarded-icon-${r.id}`} />
                          )}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono font-semibold text-slate-700">
                        {r.invoice_no || <span className="text-slate-300 font-normal">—</span>}
                      </td>
                      <td className="px-3 py-2" data-testid={`or-audit-parcel-${r.id}`}>
                        {r.parcel ? (<div className="space-y-0.5">
                          {r.parcel.customer_name && <div className="font-semibold text-slate-800">{r.parcel.customer_name}{r.parcel.code ? <span className="font-normal text-slate-400"> · {r.parcel.code}</span> : null}</div>}
                          {r.parcel.tracking
                            ? <a href={`/dashboard?panel=order_register&or_search=${encodeURIComponent(r.parcel.tracking)}`} target="_blank" rel="noopener noreferrer"
                                 title="Open this parcel in Order Register (new tab)"
                                 className="block font-mono text-[11px] text-indigo-700 underline decoration-dotted underline-offset-2 hover:text-indigo-900" data-testid={`or-audit-tracking-${r.id}`}>{r.parcel.tracking} ↗</a>
                            : <div className="font-mono text-[11px] text-slate-300" data-testid={`or-audit-tracking-${r.id}`}>no tracking</div>}
                          {r.parcel.medicine && <div className="text-[11px] text-slate-600">{r.parcel.medicine}{r.parcel.quantity ? ` × ${r.parcel.quantity}` : ""}</div>}
                        </div>) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2" data-testid={`or-audit-reason-${r.id}`}>
                        {r.extra?.reason
                          ? <span className="inline-block rounded-md bg-amber-50 border border-amber-200 px-1.5 py-0.5 text-[11px] font-semibold text-amber-900">{r.extra.reason}</span>
                          : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2 text-slate-600">
                        {r.action === "bulk_delete" && (() => {
                          const s = scopeSummary(r.extra);
                          if (!s) return null;
                          return (
                            <div className={`inline-block px-2 py-0.5 mb-1 rounded text-[10px] font-semibold ${
                              s.tone === "danger"
                                ? "bg-red-600 text-white"
                                : "bg-red-50 text-red-700 border border-red-200"
                            }`} data-testid={`or-audit-scope-${r.id}`}>
                              Scope: {s.label} · {r.extra?.count ?? 0} row{(r.extra?.count ?? 0) === 1 ? "" : "s"}
                            </div>
                          );
                        })()}
                        {r.action === "packing_slip_deferred" && r.extra?.reason && (
                          <div className="text-[11px] text-rose-700 mt-0.5" data-testid={`audit-defer-reason-${r.id}`}>
                            Reason: {r.extra.reason}
                          </div>
                        )}
                        {(r.action === "bulk_delete" || r.action === "delete" || (r.action === "update" && r.extra?.before_row)) && (r.extra?.delete_batch_id || r.extra?.rows || r.extra?.before_row) && (
                          <button type="button"
                            className="inline-flex items-center gap-1 px-2 py-0.5 mb-1 ml-1 rounded text-[10px] font-semibold bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200"
                            title="Inspect the rows captured in this delete snapshot"
                            data-testid={`or-audit-viewrows-${r.id}`}
                            onClick={() => (r.extra?.rows || r.extra?.before_row)
                              ? setDelView({ rows: r.extra.rows || [r.extra.before_row], count: r.extra?.count ?? (r.extra.rows || [1]).length,
                                             deletedBy: `${r.user_name || r.user_email} · ${fmtWhen(r.created_at)}${r.extra?.reason ? ` · Reason: ${r.extra.reason}` : ""}`,
                                             title: r.action === "update" ? "Row before the change" : null })
                              : viewDeletedRows(r.extra.delete_batch_id, r.extra?.count ?? 0)}>
                            <Eye className="w-3 h-3" /> {r.action === "update" ? "View row before" : "View deleted rows"}
                          </button>
                        )}
                        {lines.length > 0 ? (
                          <div className="space-y-0.5">
                            {lines.slice(0, 8).map((l, i) => (
                              <div key={i} className="font-mono text-[10px] break-all">{l}</div>
                            ))}
                            {lines.length > 8 && (
                              <div className="text-slate-400 text-[10px]">+{lines.length - 8} more</div>
                            )}
                          </div>
                        ) : <span className="text-slate-300">—</span>}
                        {r.entry_id && (
                          <div className="text-slate-300 text-[9px] font-mono mt-0.5"
                               title={r.entry_id}>row {String(r.entry_id).slice(0, 8)}…</div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {data && !busy && data.total > data.rows.length + extraRows.length && (
              <div className="p-3 text-center border-t">
                <Button size="sm" variant="outline" onClick={loadMore} data-testid="or-audit-load-more">
                  Load more ({data.total - data.rows.length - extraRows.length} left)
                </Button>
              </div>)}
          </div>
        </div>
      </div>

      {/* Iter 207 - deleted-rows snapshot viewer */}
      <Dialog open={!!delView} onOpenChange={(o) => { if (!o) setDelView(null); }}>
        <DialogContent className="max-w-5xl" data-testid="or-audit-delrows-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">
              {delView?.title || "Deleted rows snapshot"} {delView ? `(${delView.count})` : ""}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Full rows as they were — kept with the audit entry for 365 days.
            </DialogDescription>
          </DialogHeader>
          {delView?.loading && (
            <div className="py-10 text-center text-slate-500 text-sm">
              <RefreshCw className="w-4 h-4 animate-spin inline mr-2" /> Loading…
            </div>
          )}
          {delView && !delView.loading && delView.error && (
            <p className="text-sm text-red-600" data-testid="or-audit-delrows-error">
              {delView.error}
            </p>
          )}
          {delView && !delView.loading && !delView.error && (
            <div className="max-h-[60vh] overflow-auto">
              {delView.deletedBy && (
                <p className="text-[11px] text-slate-500 mb-2">
                  Deleted by {delView.deletedBy}
                </p>
              )}
              <table className="w-full text-[11px]" data-testid="or-audit-delrows-table">
                <thead className="bg-slate-100 sticky top-0 text-slate-600 uppercase text-[9px] tracking-wide">
                  <tr>
                    <th className="px-2 py-1 text-left">Date</th>
                    <th className="px-2 py-1 text-left">Code</th>
                    <th className="px-2 py-1 text-left">Customer</th>
                    <th className="px-2 py-1 text-left">Address</th>
                    <th className="px-2 py-1 text-left">Medicine</th>
                    <th className="px-2 py-1 text-right">Qty</th>
                    <th className="px-2 py-1 text-left">Tracking</th>
                    <th className="px-2 py-1 text-left">Invoice</th>
                    <th className="px-2 py-1 text-left">Mode</th>
                  </tr>
                </thead>
                <tbody>
                  {delView.rows.map((s, i) => (
                    <tr key={s.id || i} className="border-t border-slate-100"
                        data-testid={`or-audit-delrow-${i}`}>
                      <td className="px-2 py-1 whitespace-nowrap">{s.date || "—"}</td>
                      <td className="px-2 py-1 font-mono">{s.code || "—"}</td>
                      <td className="px-2 py-1">{s.customer_name || "—"}</td>
                      <td className="px-2 py-1 max-w-[220px] whitespace-pre-line text-slate-500">{s.address || "—"}</td>
                      <td className="px-2 py-1">{s.medicine || "—"}</td>
                      <td className="px-2 py-1 text-right">{s.quantity || "—"}</td>
                      <td className="px-2 py-1 font-mono">{s.tracking || "—"}</td>
                      <td className="px-2 py-1 font-mono">{s.invoice_no || "—"}</td>
                      <td className="px-2 py-1">{s.mode || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
